import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/modules/auth/useAuth';
import { getSupabase } from '@/lib/supabase';
import { env, hasPush } from '@/config/env';

/**
 * Web Push subscription wiring.
 *
 *   permission     — current Notification.permission value (or 'unsupported').
 *   subscribed     — true if we have a row in hottr_push_subscriptions for this endpoint.
 *   subscribe()    — request permission + register with PushManager + persist row.
 *   unsubscribe()  — unregister + delete row.
 *
 * No-ops gracefully when env.vapidPublicKey is missing or the browser
 * lacks Notification/ServiceWorker/PushManager. UI should still render.
 */

export type PushPermission = 'default' | 'granted' | 'denied' | 'unsupported';

const urlBase64ToUint8Array = (base64String: string): Uint8Array => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) bytes[i] = raw.charCodeAt(i);
  return bytes;
};

const supportsPush = (): boolean =>
  typeof window !== 'undefined' &&
  'serviceWorker' in navigator &&
  'PushManager' in window &&
  'Notification' in window;

export interface PushState {
  available: boolean;
  permission: PushPermission;
  subscribed: boolean;
  busy: boolean;
  subscribe(): Promise<{ ok: boolean; error?: string }>;
  unsubscribe(): Promise<{ ok: boolean; error?: string }>;
}

export const usePushSubscription = (): PushState => {
  const { user } = useAuth();
  const available = hasPush() && supportsPush();
  const [permission, setPermission] = useState<PushPermission>(
    supportsPush() ? Notification.permission : 'unsupported',
  );
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!available) return;
    (async () => {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      setSubscribed(Boolean(sub));
    })();
  }, [available]);

  const subscribe = useCallback(async () => {
    if (!available || !user) return { ok: false, error: 'Push not available.' };
    const supabase = getSupabase();
    if (!supabase) return { ok: false, error: 'No connection.' };

    setBusy(true);
    try {
      const perm = await Notification.requestPermission();
      setPermission(perm as PushPermission);
      if (perm !== 'granted') return { ok: false, error: 'Permission denied.' };

      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        // PushManager's TS type wants BufferSource; the Uint8Array we
        // produce is fine at runtime but newer TS lib defaults narrow
        // the buffer type, so cast through BufferSource here.
        applicationServerKey: urlBase64ToUint8Array(env.vapidPublicKey) as unknown as BufferSource,
      });

      const json = sub.toJSON();
      const endpoint = json.endpoint!;
      const p256dh = json.keys?.p256dh ?? '';
      const auth = json.keys?.auth ?? '';

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const db = supabase as any;
      const { error } = (await db.from('hottr_push_subscriptions').upsert(
        {
          user_id: user.id,
          endpoint,
          p256dh,
          auth,
          user_agent: navigator.userAgent.slice(0, 200),
        },
        { onConflict: 'endpoint' },
      )) as { error: { message: string } | null };

      if (error) return { ok: false, error: error.message };
      setSubscribed(true);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'subscribe failed' };
    } finally {
      setBusy(false);
    }
  }, [available, user?.id]);

  const unsubscribe = useCallback(async () => {
    if (!available || !user) return { ok: false, error: 'Push not available.' };
    const supabase = getSupabase();
    if (!supabase) return { ok: false, error: 'No connection.' };

    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      const endpoint = sub?.endpoint;
      if (sub) await sub.unsubscribe();

      if (endpoint) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const db = supabase as any;
        await db.from('hottr_push_subscriptions').delete().eq('endpoint', endpoint);
      }
      setSubscribed(false);
      return { ok: true };
    } catch (err) {
      return { ok: false, error: err instanceof Error ? err.message : 'unsubscribe failed' };
    } finally {
      setBusy(false);
    }
  }, [available, user?.id]);

  return { available, permission, subscribed, busy, subscribe, unsubscribe };
};
